import { createStore } from "zustand/vanilla";
import { CookieConsent } from "./types";

export const CONSENT_COOKIE_NAME = "cookie_consent";

/** Поднять при изменении политики/категорий — сохранённые выборы сбросятся, баннер покажется снова. */
export const CONSENT_POLICY_VERSION = 2;

const CONSENT_MAX_AGE = 365 * 24 * 60 * 60;

const GA_COOKIE = /^(_ga|_ga_\w+|_gid|_gat\w*)$/;

/**
 * До инициализации аналитика запрещена: события, отправленные раньше
 * createCookieConsent(), не должны уйти без решения.
 */
export const consentStore = createStore<CookieConsent.State>(() => ({
  zone: "gdpr",
  country: "",
  privacyUrl: "/privacy-policy",
  analytics: false,
  advertising: false,
  decided: false,
  settings: false,
}));

export const isAnalyticsAllowed = (): boolean => {
  return consentStore.getState().analytics;
};

export const isAdvertisingAllowed = (): boolean => {
  return consentStore.getState().advertising;
};

export const openCookieSettings = () => {
  consentStore.setState({ settings: true });
};

export const closeCookieSettings = () => {
  consentStore.setState({ settings: false });
};

const readCookie = (name: string): string | undefined => {
  const prefix = `${name}=`;

  return document.cookie
    .split("; ")
    .find((pair) => pair.startsWith(prefix))
    ?.slice(prefix.length);
};

/**
 * Сохранённый выбор действителен, если совпала версия политики. В gdpr-зоне
 * принимаем только явный выбор: «Got it» из row-зоны согласием не является.
 */
export const parseConsent = (
  raw: string | undefined,
  zone: CookieConsent.Zone,
): CookieConsent.Stored | undefined => {
  if (raw === undefined) {
    return undefined;
  }

  try {
    const result = CookieConsent.StoredSchema.safeParse(
      JSON.parse(decodeURIComponent(raw)),
    );

    if (result.success === false) {
      return undefined;
    }

    if (result.data.v !== CONSENT_POLICY_VERSION) {
      return undefined;
    }

    if (zone === "gdpr" && result.data.method !== "choice") {
      return undefined;
    }

    return result.data;
  } catch {
    return undefined;
  }
};

const writeConsent = (stored: CookieConsent.Stored) => {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  const value = encodeURIComponent(JSON.stringify(stored));

  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; max-age=${CONSENT_MAX_AGE}; path=/; SameSite=Lax${secure}`;
};

/** GA ставит cookie на корневой домен — перебираем все суффиксы хоста. */
const removeGaCookies = () => {
  const parts = location.hostname.split(".");
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

  document.cookie
    .split("; ")
    .map((pair) => pair.split("=")[0])
    .filter((name) => name !== undefined && GA_COOKIE.test(name))
    .forEach((name) => {
      document.cookie = `${name}=; ${expired}`;

      parts.forEach((_, index) => {
        document.cookie = `${name}=; ${expired}; domain=${parts.slice(index).join(".")}`;
      });
    });
};

/**
 * gtag('consent', 'default', ...) задаёт бэкенд до загрузки GA (с wait_for_update,
 * см. CookieConsentReader::gtagConsentDefaults в inflact), здесь только update.
 * Analytics закрывает GA/Mixpanel-поля, Advertising — рекламные поля Google Ads
 * (dev/inflact#6510): раньше ad_storage/ad_user_data/ad_personalization были
 * жёстко привязаны к Analytics и оставались denied даже после согласия.
 * gtag может быть вырезан блокировщиком.
 */
const updateGtag = (analytics: boolean, advertising: boolean) => {
  try {
    const analyticsValue = analytics ? "granted" : "denied";
    const advertisingValue = advertising ? "granted" : "denied";

    window.gtag("consent", "update", {
      ad_storage: advertisingValue,
      ad_user_data: advertisingValue,
      ad_personalization: advertisingValue,
      analytics_storage: analyticsValue,
      functionality_storage: analyticsValue,
      personalization_storage: analyticsValue,
      security_storage: analyticsValue,
    });
  } catch {
    // gtag not loaded
  }
};

const setRestrictedDataProcessing = () => {
  try {
    window.gtag("set", { restricted_data_processing: true });
  } catch {
    // gtag not loaded
  }
};

export const initConsent = (config: CookieConsent.Config) => {
  const stored = parseConsent(readCookie(CONSENT_COOKIE_NAME), config.zone);
  const rowDefault = config.zone === "row";

  const analytics = stored !== undefined ? stored.analytics : rowDefault;
  const advertising = stored !== undefined ? stored.advertising : rowDefault;

  consentStore.setState({
    zone: config.zone,
    country: config.country,
    privacyUrl: config.privacyUrl,
    analytics,
    advertising,
    decided: stored !== undefined,
  });

  // Service Provider для трафика из США (CCPA/CPRA)
  if (config.country.toUpperCase() === "US") {
    setRestrictedDataProcessing();
  }

  updateGtag(analytics, advertising);
};

export const saveConsent = (
  analytics: boolean,
  advertising: boolean,
  method: CookieConsent.Method,
) => {
  writeConsent({
    v: CONSENT_POLICY_VERSION,
    necessary: true,
    analytics,
    advertising,
    method,
    ts: Date.now(),
  });

  consentStore.setState({
    analytics,
    advertising,
    decided: true,
  });

  updateGtag(analytics, advertising);

  if (analytics === false) {
    removeGaCookies();
  }
};
